import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import Badge from '../ui/Badge';
import Textarea from '../ui/Textarea';
import Spinner from '../ui/Spinner';
import { getLeadVerification, reviewLeadItems, downloadLeadDocument } from '../../api/leads';
import { pushToast } from '../ui/toastBus';

export const REASON_LABELS = { incorrect: 'Incorrect', not_clear: 'Not clear', missing: 'Missing' };
const REJECT_REASONS = [
  { value: 'incorrect', label: 'Incorrect' },
  { value: 'not_clear', label: 'Not clear' },
];

const itemId = (type, key) => `${type}:${key}`;

function ReviewBadge({ review }) {
  if (!review) return <Badge tone="gray">Pending</Badge>;
  if (review.status === 'updated') {
    return (
      <div className="text-right">
        <Badge tone="amber">Updated — re-check</Badge>
        <div className="mt-0.5 max-w-[16rem] text-xs text-gray-500">
          Was rejected: {REASON_LABELS[review.reason] || review.reason}
          {review.remark ? ` — “${review.remark}”` : ''}
        </div>
      </div>
    );
  }
  return (
    <div className="text-right">
      <Badge tone={review.status === 'approved' ? 'green' : 'red'}>
        {review.status === 'approved' ? 'Approved' : `Rejected${review.reason ? ` · ${REASON_LABELS[review.reason]}` : ''}`}
      </Badge>
      {review.remark && <div className="mt-0.5 max-w-[16rem] text-xs text-gray-500">“{review.remark}”</div>}
    </div>
  );
}

function ReasonPicker({ value, onChange }) {
  return (
    <div className="flex flex-wrap items-center gap-4 text-sm text-gray-700">
      <span className="font-medium">Reason:</span>
      {REJECT_REASONS.map((r) => (
        <label key={r.value} className="flex items-center gap-1.5">
          <input type="radio" name="reject-reason" checked={value === r.value} onChange={() => onChange(r.value)} />
          {r.label}
        </label>
      ))}
    </div>
  );
}

const checkboxClass = 'h-4 w-4 rounded border-gray-300 text-primary';

/**
 * Remark + approve / reject for whatever is ticked. `picked` is a list of { type, key, missing }.
 * Rejecting asks for a reason unless every picked item is simply missing.
 */
function ReviewBar({ picked, busy, onSubmit, hint = 'Tick the items to approve or reject.' }) {
  const [remark, setRemark] = useState('');
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  const needsReason = picked.some((p) => !p.missing);

  const submit = (decision) => {
    onSubmit({
      items: picked.map((p) => ({ type: p.type, key: p.key })),
      decision,
      remark: remark || undefined,
      reason: decision === 'rejected' && needsReason ? reason : undefined,
    });
    setRemark('');
    setRejecting(false);
    setReason('');
  };

  return (
    <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
      <div className="mb-2 text-sm text-gray-600">
        {picked.length === 0 ? hint : <span className="font-medium text-gray-800">{picked.length} selected</span>}
      </div>
      <Textarea label="Remark (optional)" rows={2} value={remark} onChange={(e) => setRemark(e.target.value)} className="mb-3" />
      {rejecting && needsReason && (
        <div className="mb-3">
          <ReasonPicker value={reason} onChange={setReason} />
        </div>
      )}
      <div className="flex justify-end gap-3">
        {!rejecting ? (
          <>
            <Button variant="danger" disabled={picked.length === 0 || busy} onClick={() => (needsReason ? setRejecting(true) : submit('rejected'))}>
              Reject
            </Button>
            <Button variant="success" disabled={picked.length === 0} loading={busy} onClick={() => submit('approved')}>
              Approve
            </Button>
          </>
        ) : (
          <>
            <Button variant="secondary" onClick={() => setRejecting(false)}>
              Cancel
            </Button>
            <Button variant="danger" disabled={!reason} loading={busy} onClick={() => submit('rejected')}>
              Confirm reject
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

function CheckList({ title, rows, selected, toggle, toggleAll, render }) {
  if (rows.length === 0) return null;
  const allOn = rows.every((r) => selected.has(r.id));
  return (
    <div className="mb-4">
      <div className="mb-1.5 flex items-center gap-2">
        <input type="checkbox" checked={allOn} onChange={() => toggleAll(rows, allOn)} aria-label={`Select all ${title}`} className={checkboxClass} />
        <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          {title} ({rows.length})
        </h4>
      </div>
      <div className="divide-y divide-gray-100 rounded-md border border-gray-200">
        {rows.map((r) => (
          <label key={r.id} className="flex cursor-pointer items-start gap-3 px-3 py-2 hover:bg-gray-50">
            <input type="checkbox" checked={selected.has(r.id)} onChange={() => toggle(r.id)} className={`mt-0.5 ${checkboxClass}`} />
            <div className="flex flex-1 items-start justify-between gap-3">
              {render(r)}
              <ReviewBadge review={r.item.review} />
            </div>
          </label>
        ))}
      </div>
    </div>
  );
}

/** Downloads a lead document once (shared by its thumbnail and the enlarged view). */
function useDocumentFile(doc) {
  const ext = (doc.original_name?.split('.').pop() || '').toLowerCase();
  const isPdf = doc.mime_type === 'application/pdf' || ext === 'pdf';
  const isImage =
    !isPdf && (doc.mime_type?.startsWith('image/') || ['jpg', 'jpeg', 'png', 'webp'].includes(ext)) && !/hei[cf]/.test(`${doc.mime_type}${ext}`);

  const query = useQuery({
    queryKey: ['lead-document-file', doc.id],
    queryFn: async () => {
      const response = await downloadLeadDocument(doc.id);
      return URL.createObjectURL(isPdf ? new Blob([response.data], { type: 'application/pdf' }) : response.data);
    },
    staleTime: Infinity,
    gcTime: 5 * 60 * 1000,
  });

  return { url: query.data, isLoading: query.isLoading, isError: query.isError, isPdf, isImage };
}

function Preview({ doc, large = false }) {
  const file = useDocumentFile(doc);
  if (file.isLoading) return <Spinner />;
  if (!file.url) return <span className="px-2 text-center text-xs text-gray-400">Preview unavailable</span>;
  if (file.isImage) return <img src={file.url} alt={doc.original_name} className={large ? 'max-h-full max-w-full object-contain' : 'h-full w-full object-cover'} />;
  if (file.isPdf) {
    return large ? (
      <div className="flex h-full w-full flex-col">
        <iframe title={doc.original_name} src={file.url} className="min-h-0 flex-1" />
        <a href={file.url} target="_blank" rel="noreferrer" className="shrink-0 bg-white py-1 text-center text-xs text-primary hover:underline">
          PDF not showing? Open in a new tab
        </a>
      </div>
    ) : (
      // First page only; clicks fall through to the card button.
      <iframe title={doc.original_name} src={`${file.url}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`} className="pointer-events-none h-full w-full" />
    );
  }
  return large ? (
    <a href={file.url} download={doc.original_name} className="text-sm font-medium text-primary hover:underline">
      Preview not available — download {doc.original_name}
    </a>
  ) : (
    <span className="px-2 text-center text-xs text-gray-400">{doc.original_name}</span>
  );
}

/** One tile of the documents grid: preview, name and review state. Uploaded tiles open the enlarged view. */
function DocumentCard({ doc, checked, onCheck, onOpen, fieldCount }) {
  const missing = doc.missing;
  return (
    <div className={`overflow-hidden rounded-md border bg-white ${checked ? 'border-primary ring-1 ring-primary' : 'border-gray-200'}`}>
      <div className="flex items-center gap-2 border-b border-gray-100 px-2 py-1.5">
        <input type="checkbox" checked={checked} onChange={onCheck} aria-label={`Select ${doc.label}`} className={checkboxClass} />
        <span className="truncate text-sm font-medium text-gray-900" title={doc.label}>
          {doc.label}
        </span>
      </div>
      <button
        type="button"
        disabled={missing}
        onClick={onOpen}
        className="flex h-36 w-full items-center justify-center overflow-hidden bg-gray-50 enabled:hover:bg-gray-100"
      >
        {missing ? <span className="text-xs text-amber-700">Not uploaded</span> : <Preview doc={doc} />}
      </button>
      <div className="flex items-center justify-between gap-2 px-2 py-1.5">
        <span className="text-xs text-gray-500">{missing ? 'Missing' : `${fieldCount} field${fieldCount === 1 ? '' : 's'} to check`}</span>
        <ReviewBadge review={doc.review} />
      </div>
    </div>
  );
}

/**
 * Enlarged document beside the fields linked to it (picked on Workflow Documents). The document and
 * each field can be ticked and approved / rejected together; fields shared with other documents
 * carry their review across because reviews are keyed by field, not by document.
 */
function DocumentViewer({ doc, fields, others, busy, onBack, onOpenDoc, onReview }) {
  const [selected, setSelected] = useState(() => new Set());
  const rows = [{ id: itemId('document', doc.key), type: 'document', key: doc.key, missing: false }, ...fields.map((f) => ({ id: itemId('field', f.key), type: 'field', key: f.key, missing: f.missing }))];
  const picked = rows.filter((r) => selected.has(r.id));
  const allOn = rows.every((r) => selected.has(r.id));

  const toggle = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const index = others.findIndex((d) => d.key === doc.key);
  const go = (offset) => onOpenDoc(others[index + offset]);

  return (
    <div>
      <div className="mb-3 flex items-center justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-gray-900">{doc.label}</div>
          <div className="text-xs text-gray-500">
            {doc.original_name}
            {doc.uploaded_by ? ` · uploaded by ${doc.uploaded_by}` : ''}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" className="px-3 py-1.5 text-xs" disabled={index <= 0} onClick={() => go(-1)}>
            ← Prev
          </Button>
          <span className="text-xs text-gray-500">
            {index + 1} / {others.length}
          </span>
          <Button variant="secondary" className="px-3 py-1.5 text-xs" disabled={index >= others.length - 1} onClick={() => go(1)}>
            Next →
          </Button>
          <Button variant="secondary" className="px-3 py-1.5 text-xs" onClick={onBack}>
            Back to grid
          </Button>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_22rem]">
        <div className="flex h-[62vh] items-center justify-center overflow-hidden rounded-md border border-gray-200 bg-gray-50">
          <Preview doc={doc} large />
        </div>

        <div className="flex max-h-[62vh] flex-col gap-3 overflow-y-auto">
          <div>
            <div className="mb-1.5 flex items-center gap-2">
              <input type="checkbox" checked={allOn} onChange={() => setSelected(allOn ? new Set() : new Set(rows.map((r) => r.id)))} aria-label="Select all" className={checkboxClass} />
              <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Cross-check against the document</h4>
            </div>
            <div className="divide-y divide-gray-100 rounded-md border border-gray-200">
              <label className="flex cursor-pointer items-start gap-3 px-3 py-2 hover:bg-gray-50">
                <input type="checkbox" checked={selected.has(rows[0].id)} onChange={() => toggle(rows[0].id)} className={`mt-0.5 ${checkboxClass}`} />
                <div className="flex flex-1 items-start justify-between gap-2">
                  <div className="text-sm font-medium text-gray-900">This document</div>
                  <ReviewBadge review={doc.review} />
                </div>
              </label>
              {fields.map((f) => (
                <label key={f.key} className="flex cursor-pointer items-start gap-3 px-3 py-2 hover:bg-gray-50">
                  <input type="checkbox" checked={selected.has(itemId('field', f.key))} onChange={() => toggle(itemId('field', f.key))} className={`mt-0.5 ${checkboxClass}`} />
                  <div className="flex flex-1 items-start justify-between gap-2">
                    <div>
                      <div className="text-xs text-gray-500">{f.label}</div>
                      <div className="text-sm font-medium text-gray-900">
                        {f.value || (f.doc_only ? <span className="text-gray-400">Not on lead — optional check</span> : <span className="text-amber-700">Not provided</span>)}
                      </div>
                    </div>
                    <ReviewBadge review={f.review} />
                  </div>
                </label>
              ))}
            </div>
            {fields.length === 0 && <p className="mt-2 text-xs text-gray-400">No fields are linked to this document.</p>}
          </div>

          <ReviewBar picked={picked} busy={busy} onSubmit={(payload) => onReview(payload, () => setSelected(new Set()))} />
        </div>
      </div>
    </div>
  );
}

// "Updated" (fixed after a rejection) still needs checking, so it counts as pending.
const statusOf = (item) => (item.review?.status === 'approved' ? 'approved' : item.review?.status === 'rejected' ? 'rejected' : 'pending');
const STATUSES = ['pending', 'rejected', 'approved'];
const cap = (w) => w[0].toUpperCase() + w.slice(1);

export default function VerifyLeadModal({ lead, onClose }) {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState('documents');
  const [status, setStatus] = useState('pending');
  const [selected, setSelected] = useState(() => new Set());
  const [viewingKey, setViewingKey] = useState(null);

  const query = useQuery({
    queryKey: ['lead-verification', lead?.id],
    queryFn: () => getLeadVerification(lead.id),
    enabled: !!lead,
  });
  const data = query.data;
  const uploaded = useMemo(() => data?.uploaded_documents || [], [data]);
  const missingDocs = useMemo(() => data?.missing_documents || [], [data]);

  useEffect(() => {
    setTab('documents');
    setStatus('pending');
    setSelected(new Set());
    setViewingKey(null);
  }, [lead?.id]);

  const mutation = useMutation({
    mutationFn: (payload) => reviewLeadItems(lead.id, payload),
    onSuccess: (result, payload) => {
      queryClient.setQueryData(['lead-verification', lead.id], result);
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['rejected-applicants'] });
      pushToast({ type: 'success', message: `${payload.items.length} item(s) ${payload.decision}.` });
    },
    onError: (err) => pushToast({ type: 'error', message: err.response?.data?.errors?.reason?.[0] || err.response?.data?.message || 'Could not save the review.' }),
  });

  const review = (payload, after) =>
    mutation.mutate(payload, {
      onSuccess: () => {
        setSelected(new Set());
        after?.();
      },
    });

  const fieldsByKey = useMemo(() => Object.fromEntries((data?.fields || []).map((f) => [f.key, f])), [data]);
  const fieldsOf = (doc) => (doc.field_keys || []).map((k) => fieldsByKey[k]).filter(Boolean);

  // Fields no uploaded document points at, so nothing escapes verification.
  const unlinkedFields = useMemo(() => {
    const linked = new Set(uploaded.flatMap((d) => d.field_keys || []));
    return (data?.fields || []).filter((f) => !f.doc_only && !linked.has(f.key));
  }, [data, uploaded]);

  const row = (type, item) => ({ id: itemId(type, item.key), type, key: item.key, missing: !!item.missing, item });
  const docRows = [...uploaded, ...missingDocs].map((d) => row('document', d));
  const fieldRows = (data?.fields || []).map((f) => row('field', f));
  const unlinkedRows = unlinkedFields.map((f) => row('field', f));

  const groups = [
    { value: 'documents', label: 'Documents', kind: 'documents', all: docRows },
    { value: 'fields', label: 'Fields', kind: 'fields', all: fieldRows },
    { value: 'unlinked', label: 'Not linked fields', kind: 'fields', all: unlinkedRows },
  ];
  const group = groups.find((g) => g.value === tab) || groups[0];
  const countOf = (g, st) => g.all.filter((r) => statusOf(r.item) === st).length;
  const current = { ...group, rows: group.all.filter((r) => statusOf(r.item) === status) };
  const picked = current.rows.filter((r) => selected.has(r.id));

  const toggle = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const toggleAll = (list, allOn) =>
    setSelected((prev) => {
      const next = new Set(prev);
      list.forEach((r) => (allOn ? next.delete(r.id) : next.add(r.id)));
      return next;
    });

  const switchTab = (next) => {
    setTab(next);
    setSelected(new Set());
  };
  const switchStatus = (next) => {
    setStatus(next);
    setSelected(new Set());
  };

  const viewing = viewingKey ? uploaded.find((d) => d.key === viewingKey) : null;
  const statusTone = data?.status === 'verified' ? 'green' : data?.status === 'rejected' ? 'red' : 'gray';

  const grid = (rows) => (
    <div className="mb-4">
      <div className="mb-1.5 flex items-center gap-2">
        <input
          type="checkbox"
          checked={rows.every((r) => selected.has(r.id))}
          onChange={() => toggleAll(rows, rows.every((r) => selected.has(r.id)))}
          aria-label="Select all documents"
          className={checkboxClass}
        />
        <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Documents ({rows.length})</h4>
      </div>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
        {rows.map((r) => (
          <DocumentCard
            key={r.id}
            doc={r.item}
            checked={selected.has(r.id)}
            onCheck={() => toggle(r.id)}
            onOpen={() => setViewingKey(r.key)}
            fieldCount={fieldsOf(r.item).length}
          />
        ))}
      </div>
    </div>
  );

  return (
    <Modal
      open={!!lead}
      onClose={onClose}
      size="2xl"
      title={lead ? `Verify — ${lead.full_name} (${lead.reference_no})` : ''}
      footer={
        viewing ? null : (
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        )
      }
    >
      {query.isLoading || !data ? (
        <div className="py-10 text-center">
          <Spinner className="mx-auto" />
        </div>
      ) : viewing ? (
        <DocumentViewer
          key={viewing.key}
          doc={viewing}
          fields={fieldsOf(viewing)}
          others={uploaded}
          busy={mutation.isPending}
          onBack={() => setViewingKey(null)}
          onOpenDoc={(d) => setViewingKey(d.key)}
          onReview={review}
        />
      ) : (
        <>
          <div className="mb-3 flex items-center justify-between gap-3">
            <div className="flex gap-1 border-b border-gray-200">
              {groups.map((g) => {
                const pending = countOf(g, 'pending');
                return (
                  <button
                    key={g.value}
                    onClick={() => switchTab(g.value)}
                    className={`-mb-px flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium ${
                      group.value === g.value ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {g.label}
                    <span className="rounded-full bg-gray-100 px-1.5 text-xs text-gray-600">{g.all.length}</span>
                    {pending > 0 && <span className="rounded-full bg-amber-100 px-1.5 text-xs text-amber-800">{pending} pending</span>}
                  </button>
                );
              })}
            </div>
            <div className="flex shrink-0 items-center gap-2 text-sm text-gray-500">
              Verification: <Badge tone={statusTone}>{data.status}</Badge>
            </div>
          </div>

          <div className="mb-4 flex gap-2">
            {STATUSES.map((st) => (
              <button
                key={st}
                onClick={() => switchStatus(st)}
                className={`rounded-full border px-3 py-1 text-xs font-medium ${
                  status === st ? 'border-primary bg-primary text-white' : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                {cap(st)} ({countOf(group, st)})
              </button>
            ))}
          </div>

          {current.rows.length === 0 ? (
            <p className="mb-4 text-sm text-gray-400">Nothing here.</p>
          ) : current.kind === 'documents' ? (
            grid(current.rows)
          ) : (
            <CheckList
              title="Fields"
              rows={current.rows}
              selected={selected}
              toggle={toggle}
              toggleAll={toggleAll}
              render={(r) => (
                <div>
                  <div className="text-xs text-gray-500">{r.item.label}</div>
                  {r.item.value ? (
                    <div className="text-sm font-medium text-gray-900">{r.item.value}</div>
                  ) : (
                    <div className={`text-sm ${r.item.doc_only ? 'text-gray-400' : 'text-amber-700'}`}>
                      {r.item.doc_only ? 'Not on lead — optional check' : 'Not provided'}
                    </div>
                  )}
                </div>
              )}
            />
          )}

          {current.rows.length > 0 && <ReviewBar picked={picked} busy={mutation.isPending} onSubmit={(payload) => review(payload)} />}
        </>
      )}
    </Modal>
  );
}
